<script setup lang="ts">
import vPreloadImg from '@/ts/directives/v-preload-img';
import model from '@/ts/model.ts';
import { ref, watch } from 'vue';
import type { FooterData } from '@/ts/types/footer-data.ts';
import { getNavItemByID, navigateTo } from '@/ts/management/view-navigation-management';
import type { PaymentsPageConfig } from '@/ts/types/payments-page-config.ts';
import { fetchResource, getPaymentMethodsFromWebRes, paymentsPageSorting } from '@/ts/services/web-res-service.ts';
import { firstIfUndefined, getLangText, getScript, isObject, redeemLangObj, unwrapObject } from '@/ts/utils/utils';
import { globalBlurLogic, shouldAppAdvertBeDisplayed } from '@/ts/management/ui-management.ts';
import AstronautImg from '../astronaut-img/astronaut-img.vue';
import LobbySection from '../lobby-sections/lobby-section.vue';
import Btn from '../btn.vue';
import NavLink from '../nav-link.vue';
import LoadingSpinner from '../loading-spinner.vue';
import SocialMedia from './social-media.vue';
import siteLinksConfig from './site-links-config.ts';
import AppStoreLinks from '../app-store-links.vue';

const astronautImg = import.meta.glob('@/assets/astronaut.*', { eager: true, query: '?url', import: 'default' });

const storeLink = model.isIOS ? model.clientConfig.appConfig?.appstoreLink : model.clientConfig.appConfig?.playstoreLink;

const lastSectionLoading = ref<boolean>(true);
const lastSectionData = ref<FooterData | null>(null);

const vAnjSeal = {
    async mounted(element: HTMLElement){
        const anjSeal = model.clientConfig.anjSeal;
        if(!anjSeal?.url || !anjSeal?.id) return;

        await getScript(anjSeal.url);
        element.insertAdjacentHTML('beforeend', `<div id="anj-${anjSeal.id}" data-anj-seal-id="${anjSeal.id}" data-anj-image-size="64" data-anj-image-type="basic-small"></div>`);
        setTimeout(() => (window as any)?.[`anj_${anjSeal.id?.replaceAll('-', '_')}`]?.init?.(), 0);
    },
};

const vLoadLastSection = {
    mounted(element: HTMLElement){
        let t = 0;
        let observable: IntersectionObserver | undefined;

        element.watchHandlers ||= [];

        element.watchHandlers.push(watch(() => model.lang, () => {
            lastSectionLoading.value = true;
            observable?.disconnect();

            const ownObservable = observable = new IntersectionObserver(entries => {
                entries.every(entry => {
                    clearTimeout(t);
                    if(entry.isIntersecting){
                        t = setTimeout(async () => {
                            let url = model.clientConfig.webRes?.footerContent;
                            if(isObject(url)) url = redeemLangObj(url);
                            if(!url || typeof url != 'string') return false;

                            const requestedLang = model.lang;
                            let data = await fetchResource(url);
                            if(model.lang !== requestedLang) return false;

                            lastSectionData.value = data;
                            lastSectionLoading.value = false;
                            ownObservable.disconnect();
                        }, 200);
                        return false;
                    }
                    return true;
                });
            });

            ownObservable.observe(element);
        }, {immediate: true}));

        element.watchHandlers.push(() => observable?.disconnect());
    },
};

const paymentsSectionLoading = ref<boolean>(true);
const paymentsSectionData = ref<PaymentsPageConfig[] | null>(null);

const vLoadPaymentsSection = {
    mounted(element: HTMLElement){
        let t = 0;
        element.watchHandlers ||= [];
        let observer = new IntersectionObserver(entries => {
            entries.every(entry => {
                clearTimeout(t);
                if(entry.isIntersecting){
                    t = setTimeout(async () => {
                        let data = await getPaymentMethodsFromWebRes();
                        paymentsSectionData.value = data?.filter(x => x.inDeposit).sort(paymentsPageSorting.deposit) || null;
                        paymentsSectionLoading.value = false;
                        observer.disconnect();
                    }, 200);
                    return false;
                }
                return true;
            });
        });
        observer.observe(element);
        element.watchHandlers.push(() => observer.disconnect());
    },
};

const providersSectionLoading = ref<boolean>(true);
const vLoadProvidersSection = {
    mounted(element: HTMLElement){
        let t = 0;
        element.watchHandlers ||= [];
        let observer = new IntersectionObserver(entries => {
            entries.every(entry => {
                clearTimeout(t);
                if(entry.isIntersecting){
                    t = setTimeout(() => {
                        providersSectionLoading.value = false;
                        observer.disconnect();
                    }, 200);
                    return false;
                }
                return true;
            });
        });

        observer.observe(element);
        element.watchHandlers.push(() => observer.disconnect());
    },
};
</script>

<template>
    <template v-if="model.view != 'standalone-register'">
        <astronaut-img :index="3" :position="'right'" :mobile-position="'center'" :offset="10" :mobile-offset="-20" />
    </template>
    <footer v-mobile :class="{blur: globalBlurLogic}">
        <lobby-section :top-color="'neutral_10'" :bottom-color="'primary_8'" class="inner">
            <template v-if="!model.isApp && shouldAppAdvertBeDisplayed()">
                <div class="app-advert" v-mobile>
                    <template v-if="!model.isMobile">
                        <img v-preload-img="astronautImg">
                        <div class="content">
                            <div class="segment">
                                <p class="descr">{{ getLangText('DownloadOurAppLabel') }}</p>
                                <a :href="storeLink" target="_blank">
                                    <btn :skin="'secondary'">{{ getLangText('DownloadNowLabel') }}</btn>
                                </a>
                            </div>
                            <div class="segment">
                                <app-store-links :loc="'footer'" />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="content" v-mobile>
                            <div class="segment">
                                <p class="descr">{{ getLangText('DownloadOurAppLabel') }}</p>
                            </div>
                            <div class="segment">
                                <app-store-links :loc="'footer'" />
                            </div>
                            <div class="segment">
                                <a :href="storeLink" target="_blank">
                                    <btn :skin="'secondary'">{{ getLangText('DownloadNowLabel') }}</btn>
                                </a>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <div class="links-section" v-mobile>
                <template v-if="model.siteKey == 'fund'">
                    <div class="descr" v-mobile>
                        Fundalor es una nueva forma de vivir el iGaming en México: un casino más justo, más transparente y lleno de diversión. Los Fundaloris disfrutan de promociones sin requisito de apuesta y acceso a más de 3,000 juegos de proveedores top como Pragmatic Play, Play'n GO, Playson, Red Tiger, Zitro, NetEnt, Spinomena, Bomba Games y Evolution. Más juego, más libertad y cero condiciones injustas.
                    </div>
                </template>
                <template v-for="(siteLinks, siteId) in siteLinksConfig">
                    <template v-if="model.siteId == siteId">
                        <template v-for="section in siteLinks">
                            <template v-if="typeof section.mobile === 'boolean' ? (model.isMobile ? section.mobile : !section.mobile) : true">
                                <template v-if="section.type == 'links'">
                                    <div class="site-links" v-mobile>
                                        <template v-for="siteLink in section.links">
                                            <nav-link 
                                                v-bind="getNavItemByID(siteLink.viewId)" 
                                                :text-as-link="siteLink.textAsLink ? getLangText(siteLink.textAsLink) : undefined"
                                            />
                                        </template>
                                    </div>
                                </template>
                                <template v-else-if="section.type == 'social-media'">
                                    <social-media />
                                </template>
                            </template>
                        </template>
                    </template>
                </template>
            </div>
        </lobby-section>
        <lobby-section :top-color="'neutral_1'" :bottom-color="'neutral_1'" class="inner final" :cover-bottom="true">
            <template v-if="model.siteKey == 'sky'">
                <div class="anj-seal" v-anj-seal></div>
                <div class="separator" v-mobile></div>    
            </template>
            <h2>{{ getLangText('OurPaymentMethodsLabel') }}</h2>
            <div class="provider-icons" v-mobile>
                <template v-if="paymentsSectionLoading">
                    <loading-spinner v-load-payments-section />
                </template>
                <template v-else-if="paymentsSectionData">
                    <template v-for="item in paymentsSectionData">
                        <a :href="'#'" target="_blank" @click.prevent.stop>
                            <img v-preload-img="item.imgSrcFooter || item.imgSrc" :style="{width: item.footerConfig?.width}">
                        </a>
                    </template>
                </template>
            </div>
            <div class="separator" v-mobile></div>
            <h2>{{ getLangText('OurProvidersLabel') }}</h2>
            <div class="provider-icons" v-mobile>
                <template v-if="providersSectionLoading">
                    <loading-spinner v-load-providers-section />
                </template>
                <template v-else>
                    <template v-for="provider in model.clientConfig.gameProviders">
                        <template v-if="provider.footerImgUrl">
                            <a :href="provider.href" target="_blank">
                                <img v-preload-img="provider.footerImgUrl">
                            </a>
                        </template>
                    </template>
                </template>
            </div>
            <div class="separator" v-mobile></div>
            <div class="last-section" v-mobile>
                <template v-if="lastSectionLoading">
                    <loading-spinner v-load-last-section />
                </template>
                <template v-else-if="lastSectionData">
                    <div class="descr" v-html="lastSectionData.destr"></div>
                    <div class="icons">
                        <template v-for="icon in lastSectionData.icons">
                            <template v-if="icon.href">
                                <a :href="icon.href" target="_blank">
                                    <img v-preload-img="icon.img">
                                </a>
                            </template>
                            <template v-else-if="icon.view">
                                <a href="#" @click.prevent.stop="navigateTo(icon.view)">
                                    <img v-preload-img="icon.img">
                                </a>
                            </template>
                            <template v-else>
                                <img v-preload-img="icon.img">
                            </template>
                        </template>
                    </div>
                </template>
            </div>
        </lobby-section>
    </footer>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    footer{
        position: relative;
        width: 100%;
        min-height: 500px;
        z-index: 10;
        &[data-mobile]{
            padding-bottom: 80px;
        }
        &.blur{
            opacity: .15;
        }
        & .inner{
            position: relative;
            &.final{
                padding: 20px 0 !important;
                & :deep(.content){
                    max-width: none !important;
                }
            }
            & h2{
                color: #000;
                text-align: center;
                padding-top: 50px;
                padding-bottom: 20px;
                font-size: 16px;
            }
            &:nth-child(2)[data-mobile]{
                padding-top: 30px;
            }
            & .separator{
                max-width: 100% !important;
                width: 100%;
                height: 2px;
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 2'%3E%3Cline x1='0' y1='1' x2='100' y2='1' stroke='%23555' stroke-width='2' stroke-dasharray='8 6'/%3E%3C/svg%3E");
                background-repeat: repeat-x;
                background-position: bottom left;
                opacity: .5;
                @include shared.skew-transition();
            }
            &:nth-child(1){
                padding: 50px 0 20px 0 !important;
                &[data-mobile]{
                    padding-top: 80px !important;
                }
            }
            & > :deep(.content) > *:not(.background){
                max-width: shared.$mobile-width;
                margin: 0 auto;
                @media (max-width: shared.$mobile-width){
                    max-width: 100vw;
                }
            }
            & .app-advert{
                position: relative;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding-bottom: 40px;
                &[data-mobile]{
                    & > img{
                        width: 70px;
                    }
                }
                &:not([data-mobile]){
                    &:after{
                        position: absolute;
                        display: block;
                        content: "";
                        width: 100%;
                        height: 1px;
                        bottom:0;
                        left:0;
                        @include shared.site() using($site-key){
                            background: linear-gradient(90deg, transparent 0%, shared.theme-color('comp2_4', $site-key) 50%, transparent 100%);
                        }
                    }
                }
                & > .content{
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex: 1;
                    &[data-mobile]{
                        flex-direction: column;
                        & > .segment{
                            & > a{
                                &:link,
                                &:visited,
                                &:active,
                                &:hover{
                                    text-decoration: none;
                                }
                            }
                            &:nth-child(2){
                                padding-top: 10px;
                                padding-bottom: 25px;
                            }
                        }
                    }
                    & > .segment{
                        &:nth-child(1){
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;
                            width: 100%;
                            & > a{
                                &:link,
                                &:visited,
                                &:active,
                                &:hover{
                                    text-decoration: none;
                                }
                            }
                            @include shared.site() using($site-key){
                                color: shared.theme-color("comp3_3", $site-key);
                            }
                            @media (max-width: shared.$mobile-width){
                                flex-direction: column;
                                text-align: center;
                            }
                            & > .descr{
                                @include shared.casino-section-title();
                                font-size: 25px;
                                padding: 0 30px;
                                @media (max-width: shared.$mobile-width){
                                    font-size: 20px;
                                    margin-top: 5px;
                                    margin-bottom: 5px;
                                }
                            }
                        }
                        &:nth-child(2){
                            display: flex;
                            justify-content: space-around;
                            align-items: center;
                            @media (max-width: shared.$mobile-width){
                                flex-direction: column-reverse;
                                text-align: center;
                            }
                        }
                    }
                }
            }
            & .links-section{
                display: flex;
                padding-top: 40px;
                padding-bottom: 70px;
                font-size: 14px;
                line-height: 22px;
                font-weight: 700;
                justify-content: center;
                &[data-mobile]{
                    flex-direction: column;
                    align-items: center;
                    padding-top: 0 !important;
                }
                & a{
                    &:link,
                    &:active,
                    &:visited{
                        transition: color .25s;
                        text-decoration: none;
                        @include shared.site() using($site-key){
                            color: shared.theme-color("comp2_3", $site-key);
                        }
                    }
                    &:hover{
                        @include shared.site() using($site-key){
                            color: shared.theme-color("comp3_3", $site-key);
                        }
                    }
                }
                & > .descr{
                    flex: 1;
                    font-weight: 500;
                    &[data-mobile]{
                        text-align: center;
                        padding: 0 20px;
                        padding-bottom: 20px;
                    }
                }
                & > .site-links{
                    flex: none;
                    display: flex;
                    flex-direction: column;
                    padding-right: 0 !important;
                    font-weight: 500;
                    & > *{
                        white-space: nowrap;
                    } 
                    &[data-mobile]{
                        flex: 1;
                        padding: 0 !important;
                        text-align: center;
                        & > *{
                            padding-bottom: 5px;
                        }
                    }
                    &:not([data-mobile]){
                        padding-left: 50px;
                    }
                }
            }

            & .anj-seal{
                display: flex;
                justify-content: center;
                align-items: center;
            }

            & .provider-icons{
                display:flex;
                flex-wrap: wrap;
                justify-content: center;
                align-items: center;
                & > a{
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    & > img{
                        height: 40px;
                        max-width: 90px;
                        margin: 10px;
                        object-fit: contain;
                    }
                }
            }

            & .last-section{
                display: flex;
                color: #000;
                @media (max-width: shared.$mobile-width){
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    padding-left: 10px !important;
                    padding-right: 10px !important;
                }
                & > .descr{
                    display: flex;
                    font-size: 12px;
                    line-height: 22px;
                    font-weight: 500;
                    align-items: center;
                    @media (max-width: shared.$mobile-width){
                        flex: 1;
                    }
                }
                & > .icons{
                    display:flex;
                    flex: 1.15;
                    justify-content: flex-end;
                    align-items: center;
                    & img{
                        margin: 20px;
                        height: 50px;
                    }
                    @media (max-width: shared.$mobile-width){
                        flex: 1;
                        justify-content: center;
                        flex-wrap: wrap;
                    }
                }
            }

            & .provider-icons,
            & .last-section{
                padding: 20px 0;
                padding-top: 0;
            }
            & .last-section{
                padding-top: 40px;
            }
        }
    }
</style>